import { type ReactElement, memo, Suspense, useEffect, type ReactNode } from 'react';
import { registry } from '@/lib/registry';
import { Await, useAsyncError } from 'react-router';
import { createLogger } from '@/lib/logger';

const logger = createLogger();
import type { ComponentDesignMetadata } from '@salesforce/storefront-next-runtime/design/react';
import { useComponentDataById } from './component-data-context';
import type { ComponentType } from './index';

export interface ComponentProps {
    component: ComponentType;
    className?: string;
    regionId: string;
    /** Page-region fallback used while component data or modules are still loading. */
    suspenseFallback?: ReactNode;
}

function isPromiseLike(value: unknown): value is Promise<unknown> {
    return (
        (typeof value === 'object' || typeof value === 'function') &&
        value !== null &&
        typeof (value as Promise<unknown>).then === 'function'
    );
}

/**
 * Error handler component that logs component data loading errors and renders nothing.
 * Uses React Router's useAsyncError to access the error from the Await boundary.
 *
 * When a component's data fails to load (e.g., API error), we render nothing (null)
 * instead of showing a misleading skeleton/fallback. The error is logged to the console
 * for debugging purposes.
 */
function ComponentErrorFallback({ componentId, componentTypeId }: { componentId: string; componentTypeId: string }) {
    const error = useAsyncError();

    useEffect(() => {
        logger.error(`Failed to load data for component "${componentId}" (${componentTypeId})`, { error });
    }, [componentId, componentTypeId, error]);

    // Render nothing when data loading fails
    return null;
}

function ResolvedComponent({
    component,
    className,
    regionId,
    data,
    designMetadata,
}: ComponentProps & { data: unknown; designMetadata: ComponentDesignMetadata }) {
    // CMS pages can still reference retired component types. Preloading an unknown
    // type creates a fresh rejected promise on every SSR retry and never settles.
    if (!registry.has(component.typeId)) {
        logger.warn(`Skipping unregistered Page Designer component "${component.typeId}" (${component.id})`);
        return null;
    }
    const DynamicComponent = registry.getComponent(component.typeId);
    if (!DynamicComponent) {
        // eslint-disable-next-line @typescript-eslint/only-throw-error
        throw registry.preload(component.typeId);
    }

    return (
        <DynamicComponent
            {...(component.data ?? {})}
            designMetadata={designMetadata}
            component={component}
            data={data}
            className={className}
            regionId={regionId}
        />
    );
}

export const Component = memo(function Component({
    component,
    className,
    regionId,
    suspenseFallback,
}: ComponentProps): ReactElement {
    const componentData = useComponentDataById(component.id);
    const FallbackComponent = registry.getFallback(component.typeId);
    const resolvedSuspenseFallback: ReactNode = FallbackComponent ? (
        <FallbackComponent {...(component.data ?? {})} />
    ) : (
        (suspenseFallback ?? null)
    );
    const sourceDesignMetadata = component.designMetadata as { name?: string } | undefined;
    const designMetadata: ComponentDesignMetadata = {
        name: sourceDesignMetadata?.name,
        isFragment: Boolean(component.fragment),
        isVisible: Boolean(component.visible),
        isLocalized: Boolean(component.localized),
        id: component.id,
        contentLinkUuid: component.contentLinkUuid ?? component.id,
    };

    return (
        <Suspense fallback={resolvedSuspenseFallback}>
            {isPromiseLike(componentData) ? (
                <Await
                    resolve={componentData}
                    errorElement={
                        <ComponentErrorFallback componentId={component.id} componentTypeId={component.typeId} />
                    }>
                    {(data) => (
                        <ResolvedComponent
                            component={component}
                            className={className}
                            regionId={regionId}
                            data={data}
                            designMetadata={designMetadata}
                        />
                    )}
                </Await>
            ) : (
                <ResolvedComponent
                    component={component}
                    className={className}
                    regionId={regionId}
                    data={componentData}
                    designMetadata={designMetadata}
                />
            )}
        </Suspense>
    );
});
